import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";

import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import UtmBanner from "@/components/landing/UtmBanner";
import Footer from "@/components/landing/Footer";
import FinalCTA from "@/components/landing/FinalCTA";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/landing/Breadcrumbs";

/**
 * HubPage — index/overview page that lists a collection of sub-pages.
 * items: [{ slug, title, lead?, eyebrow?, badge? }]
 */
export const HubPage = ({
  seo,
  path,
  eyebrow,
  title,
  intro,
  items = [],
  itemBasePath,
  breadcrumbs,
}) => {
  const { t } = useTranslation();
  return (
    <main data-testid={`hub-${path.replace(/\//g, "")}`} className="bg-background">
      <Seo
        title={seo.title}
        description={seo.description}
        path={path}
        keywords={seo.keywords}
        breadcrumbs={breadcrumbs}
      />
      <AnnouncementBar />
      <Header />
      <UtmBanner />
      <Breadcrumbs items={breadcrumbs} />

      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
          {eyebrow}
        </p>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] text-balance sm:text-5xl lg:text-6xl">
          {title}
        </h1>
        {intro && (
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[hsl(var(--ink-soft))]">
            {intro}
          </p>
        )}

        <ul className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <li key={it.slug}>
              <Link
                to={`${itemBasePath}/${it.slug}`}
                data-testid={`hub-item-${it.slug}`}
                className="group flex h-full flex-col justify-between overflow-hidden rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] transition-all hover:-translate-y-1 hover:border-[hsl(var(--forest))]"
              >
                {it.image && (
                  <div className="aspect-[16/10] w-full overflow-hidden bg-[hsl(var(--surface-secondary))]">
                    <img
                      src={it.image}
                      alt={it.title}
                      loading="lazy"
                      className="block h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                )}
                <div className="flex h-full flex-col justify-between p-7">
                <div>
                  {it.eyebrow && (
                    <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
                      {it.eyebrow}
                    </p>
                  )}
                  <h2 className="mt-3 font-display text-2xl font-medium leading-tight text-[hsl(var(--ink))]">
                    {it.title}
                  </h2>
                  {it.lead && (
                    <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-[hsl(var(--ink-soft))]">
                      {it.lead}
                    </p>
                  )}
                </div>
                <div className="mt-8 flex items-center justify-between text-sm text-[hsl(var(--forest))]">
                  <span>{t("common.readMore")}</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.8} />
                </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <FinalCTA />
      <Footer />
    </main>
  );
};

export default HubPage;
